import { IconName, Icon } from "@/components";
import { toDay, parseTime } from "@/lib/datetime";
import { useI18n } from "@/lib/i18n/i18n_provider";
import {
  formatMeetingRoomDateRange,
  formatDayRange,
} from "@/offering/format_date_range";
import { MeetingRoomDateRange } from "@/offering/order_params";
import { Spacer } from "@/offering/spacer";
import { TimePicker } from "@/offering/time_picker";
import { addHours } from "@/offering/time_utils";
import { getAvailableDays } from "@/offering_v2/utils/get_available_days";
import classNames from "classnames";
import { startOfMonth, endOfMonth, parseISO } from "date-fns";
import React, { useState, useEffect } from "react";
import { Dropdown, Row, Button as BTButton } from "react-bootstrap";
import { colors } from "../colors";
import {
  DateTimeValue,
  isHourlyValue,
} from "../date-time-picker/date_time_picker";
import {
  DateRangePicker,
  PickingRange,
} from "../date_range_picker/date_range_picker";
import { DateTimeFilterDropdown } from "./date_time_filter_dropdown";
import styles from "./desktop_filters.module.css";
import { Button } from "@/offering/button";

interface DesktopDateTimeFilterItemProps {
  iconName: IconName;
  onChange: (value: MeetingRoomDateRange) => void;
  value: MeetingRoomDateRange;
  label: string;
  openAt?: string;
  closedAt?: string;
  timeSlotIncrement?: number;
}

export function DesktopDateTimeFilterItem({
  iconName,
  onChange,
  value,
  label,
  openAt,
  closedAt,
  timeSlotIncrement,
}: DesktopDateTimeFilterItemProps) {
  const { fbt, locale } = useI18n();
  const [localValue, setLocalValue] = useState<MeetingRoomDateRange | null>(
    value
  );
  const today = new Date();
  const [viewingRange, setViewRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>({ startDate: startOfMonth(today), endDate: endOfMonth(today) });

  const [showCalendar, setShowCalendar] = useState(false);
  const displayValue = formatMeetingRoomDateRange(value, fbt, locale, "");
  const toggleDropdown = () => setShowCalendar(!showCalendar);
  const availableDates = getAvailableDays(viewingRange);

  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown
        onToggle={toggleDropdown}
        show={showCalendar}
        className={classNames("checkout-box-picker", styles.dropdown)}
      >
        <Dropdown.Toggle className={styles.dropdownToggle}>
          <Icon name={iconName} />
          <Spacer size={8} direction="horizontal" />
          <b>{label}:</b>
          <Spacer size={4} direction="horizontal" />
          {displayValue ? (
            <div>{displayValue}</div>
          ) : (
            <div className={styles.placeholder}>{fbt("Any")}</div>
          )}
          <Spacer size={32} direction="horizontal" />
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {displayValue && (
          <div
            className={styles.clearBtn}
            onClick={() => {
              setLocalValue(null);
              onChange({
                date: undefined,
                startTime: undefined,
                endTime: undefined,
              });
            }}
          >
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu
          align="end"
          popperConfig={{
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  altAxis: true,
                  padding: { top: 80 },
                },
              },
            ],
          }}
        >
          <div>
            <DateRangePicker
              availableDates={availableDates}
              pickingRange={PickingRange.OneDate}
              value={{
                startDate: localValue?.date
                  ? parseISO(localValue.date)
                  : undefined,
              }}
              onChange={(value) =>
                setLocalValue({
                  date: toDay(value.startDate),
                })
              }
              onViewDates={(startDate, endDate) =>
                setViewRange({ startDate, endDate })
              }
            />
            <div className="px-3 pb-3 d-flex">
              <TimePicker
                value={localValue?.startTime}
                placeholder={fbt("Start time")}
                startTime={openAt}
                endTime={closedAt}
                step={timeSlotIncrement}
                onChange={(startTime) =>
                  setLocalValue({
                    date: localValue?.date,
                    startTime,
                    endTime: undefined,
                  })
                }
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!localValue?.date}
              />
              <Spacer size={30} direction="horizontal" />
              <TimePicker
                value={localValue?.endTime}
                step={timeSlotIncrement}
                placeholder={fbt("End time")}
                startTime={addHours(
                  localValue?.startTime || openAt || "0800",
                  1
                )}
                endTime={closedAt}
                onChange={(endTime) => {
                  setLocalValue({ ...localValue, endTime });
                }}
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!localValue?.startTime}
              />
            </div>
            <Spacer size={16} />
            <Row justifyContent="flex-end">
              <Button
                disabled={
                  !(
                    localValue?.date &&
                    localValue.startTime &&
                    localValue.endTime
                  )
                }
                onPress={() => {
                  !!localValue && onChange(localValue);
                  toggleDropdown();
                }}
                title={fbt("Save")}
                override={{
                  button: {
                    style: {
                      width: "100px",
                      margin: "0 1rem 1rem 0",
                    },
                  },
                }}
              />
            </Row>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

interface DesktopDateTimeFilterItemV2Props {
  iconName: IconName;
  onChange: (value: DateTimeValue) => void;
  value: DateTimeValue;
  label: string;
  openAt?: string;
  closedAt?: string;
  timeSlotIncrement?: number;
  disabled?: boolean;
}

export function DesktopDateTimeFilterItemV2({
  iconName,
  onChange,
  value,
  label,
  openAt,
  closedAt,
  timeSlotIncrement,
  disabled,
}: DesktopDateTimeFilterItemV2Props) {
  const { fbt, locale } = useI18n();
  const [localValue, setLocalValue] = useState<DateTimeValue | undefined>(
    value
  );
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (value) {
      setLocalValue(value);
    }
  }, [value]);

  const displayValue = isHourlyValue(localValue)
    ? formatMeetingRoomDateRange(localValue, fbt, locale, "")
    : localValue?.startDate &&
      localValue.endDate &&
      formatDayRange(
        {
          start: localValue.startDate,
          end: localValue.endDate,
        },
        fbt,
        locale
      );

  const handleSeeResults = () => {
    setShowCalendar(false);
    const defaultValue = {
      date: undefined,
      startTime: undefined,
      endTime: undefined,
      startDate: undefined,
      endDate: undefined,
    };
    onChange({ ...defaultValue, ...localValue });
  };

  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown
        show={showCalendar}
        className={classNames("checkout-box-picker", styles.dropdown)}
      >
        <Dropdown.Toggle
          className={styles.dropdownToggle}
          disabled={disabled}
          onClick={() => {
            setShowCalendar((v) => !v);
            showCalendar && handleSeeResults();
          }}
        >
          <Icon name={iconName} />
          <Spacer size={8} direction="horizontal" />
          <b>{label}:</b>
          <Spacer size={4} direction="horizontal" />
          {displayValue ? (
            <div>{displayValue}</div>
          ) : (
            <div className={styles.placeholder}>
              {fbt(
                disabled ? "Select a location to filter by date" : "Any time"
              )}
            </div>
          )}
          <Spacer size={32} direction="horizontal" />
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  altAxis: true,
                  padding: { top: 80 },
                },
              },
            ],
          }}
        >
          <div>
            <DateTimeFilterDropdown
              value={localValue}
              onChange={setLocalValue}
              openAt={openAt}
              closedAt={closedAt}
              timeSlotIncrement={timeSlotIncrement}
            />
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
              <div
                role="button"
                style={{
                  color: displayValue
                    ? "var(--bs-primary)"
                    : colors.blackMinus60,
                }}
                onClick={() => setLocalValue(undefined)}
              >
                {fbt("Clear selection")}
              </div>
              <BTButton disabled={!displayValue} onClick={handleSeeResults}>
                {fbt("See results")}
              </BTButton>
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
